/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { instanceAPI } from "@src/config/api";

export type DownloadAsOptionId = "jpeg" | "dicom" | "video" | "zip";
// image: the relevant image. series: every image of the series.
export type DownloadAsScope = "image" | "series";

export interface DownloadAsOption {
  id: DownloadAsOptionId;
  scope: DownloadAsScope;
}

export interface DownloadAsGroup {
  // Label shown only when the menu has both scopes.
  kind?: DownloadAsScope;
  options: DownloadAsOption[];
}

export interface DownloadAsSource {
  // Images only (frames of 1 or more).
  imageCount: number;
  // 0 or missing = not an image, 1 = single frame, above 1 = multi-frame.
  frames: number;
  anyOtherMultiframe: boolean;
}

export interface DownloadAsMenuData extends DownloadAsSource {
  onSelect: (option: DownloadAsOption) => void;
}

export const isMultiframe = (frames?: number) => (frames ?? 0) > 1;

export const hasImage = (frames?: number) => (frames ?? 0) >= 1;

// Images only; a series without any image keeps all instances (DICOM only).
export const getImageInstances = <T extends { numberOfFrames?: number }>(
  instances: T[]
): T[] => {
  const images = instances.filter((instance) =>
    hasImage(instance.numberOfFrames)
  );
  return images.length > 0 ? images : instances;
};

const imageOption = (id: DownloadAsOptionId): DownloadAsOption => ({
  id,
  scope: "image",
});
const seriesOption = (id: DownloadAsOptionId): DownloadAsOption => ({
  id,
  scope: "series",
});

export const getDownloadAsOptions = ({
  imageCount,
  frames,
  anyOtherMultiframe,
}: DownloadAsSource): DownloadAsGroup[] => {
  const multiframe = isMultiframe(frames);
  // Not an image: DICOM only, plus the series ZIP of the images.
  if (!hasImage(frames)) {
    if (imageCount < 1) return [{ options: [imageOption("dicom")] }];
    return [
      { kind: "image", options: [imageOption("dicom")] },
      { kind: "series", options: [seriesOption("zip")] },
    ];
  }

  const multiframeOptions = [
    imageOption("jpeg"),
    imageOption("video"),
    imageOption("zip"),
  ];

  if (imageCount <= 1) {
    return [
      {
        options: multiframe
          ? multiframeOptions
          : [imageOption("jpeg"), imageOption("dicom")],
      },
    ];
  }
  if (multiframe) {
    return [
      { kind: "image", options: multiframeOptions },
      { kind: "series", options: [seriesOption("zip")] },
    ];
  }
  if (anyOtherMultiframe) {
    return [
      { kind: "image", options: [imageOption("jpeg"), imageOption("dicom")] },
      { kind: "series", options: [seriesOption("zip")] },
    ];
  }
  // Stack of single-frame images.
  return [
    {
      options: [
        imageOption("jpeg"),
        seriesOption("zip"),
        seriesOption("video"),
      ],
    },
  ];
};

// Keys under "viewer_modal".
export const getDownloadAsDescriptionKey = (
  { id, scope }: DownloadAsOption,
  { imageCount, frames }: DownloadAsSource
) => {
  if (id === "jpeg") {
    if (isMultiframe(frames)) return "download_frame_as_jpeg";
    return imageCount > 1 ? "download_image_as_jpeg" : "download_as_jpeg_image";
  }
  if (id === "dicom") return "download_as_dicom";
  if (id === "video") {
    return scope === "image"
      ? "download_all_frames_as_video"
      : "download_as_mp4_video";
  }
  return scope === "image"
    ? "download_all_frames_as_zip"
    : "download_all_images_as_jpeg";
};

interface IDownloadAsUrlParams {
  studyId: string;
  seriesId: string;
  instanceId: string;
  option: DownloadAsOption;
  frames: number;
  // 1-based, for the JPEG of a multi-frame image.
  frame?: number;
}

export const getDownloadAsUrl = ({
  studyId,
  seriesId,
  instanceId,
  option,
  frames,
  frame = 1,
}: IDownloadAsUrlParams) => {
  const { id, scope } = option;
  switch (id) {
    case "jpeg":
      return instanceAPI.DOWNLOAD_INSTANCE_JPEG(
        studyId,
        seriesId,
        instanceId,
        isMultiframe(frames) ? frame : undefined
      );
    case "dicom":
      return instanceAPI.DOWNLOAD_INSTANCE_DICOM(studyId, seriesId, instanceId);
    case "video":
      return scope === "image"
        ? instanceAPI.DOWNLOAD_FRAMES_AS_VIDEO(studyId, seriesId, instanceId)
        : instanceAPI.DOWNLOAD_SERIES_AS_VIDEO(studyId, seriesId);
    case "zip":
    default:
      return scope === "image"
        ? instanceAPI.DOWNLOAD_FRAMES_AS_ZIP(studyId, seriesId, instanceId)
        : instanceAPI.DOWNLOAD_SERIES_AS_ZIP(studyId, seriesId);
  }
};
